/**
 * CampaignDetail page - view campaign details and statistics
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { CampaignStats } from '../../components/marketing/CampaignStats';
import { EmailPreview } from '../../components/marketing/EmailPreview';
import {
  getCampaign,
  getCampaignRecipients,
  sendCampaign,
  cancelCampaign,
  type Campaign,
  type CampaignRecipient,
} from '../../services/marketingApi';
import { useToast } from '../../contexts/ToastContext';
import { ArrowLeft, Send, Trash2, RefreshCw } from 'lucide-react';

export function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [recipients, setRecipients] = useState<CampaignRecipient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const loadCampaign = async () => {
    if (!id) return;
    try {
      const data = await getCampaign(id);
      setCampaign(data);
    } catch {
      toast.error('Error al cargar campaña');
      navigate('/marketing');
    } finally {
      setIsLoading(false);
    }
  };

  const loadRecipients = async () => {
    if (!id) return;
    try {
      const data = await getCampaignRecipients(id);
      setRecipients(data.recipients);
    } catch {
      // Silently fail
    } finally {
      setIsLoadingRecipients(false);
    }
  };

  useEffect(() => {
    loadCampaign();
    loadRecipients();
  }, [id]);

  const handleSend = async () => {
    if (!id) return;
    setIsSending(true);
    try {
      await sendCampaign(id);
      toast.success('Campaña enviada - los emails se están enviando');
      await loadCampaign();
    } catch {
      toast.error('Error al enviar campaña');
    } finally {
      setIsSending(false);
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    if (!confirm('¿Estás seguro de cancelar esta campaña?')) return;

    setIsCancelling(true);
    try {
      await cancelCampaign(id);
      toast.success('Campaña cancelada');
      navigate('/marketing');
    } catch {
      toast.error('Error al cancelar campaña');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleRefresh = () => {
    loadCampaign();
    loadRecipients();
  };

  const getStatusBadge = (status: Campaign['status']) => {
    switch (status) {
      case 'draft':
        return <Badge variant="secondary">Borrador</Badge>;
      case 'sent':
        return <Badge variant="default">Enviada</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelada</Badge>;
    }
  };

  const getRecipientStatusBadge = (status: CampaignRecipient['email_status']) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      queued: 'secondary',
      sent: 'secondary',
      delivered: 'default',
      opened: 'default',
      clicked: 'default',
      bounced: 'destructive',
    };
    const labels: Record<string, string> = {
      queued: 'En cola',
      sent: 'Enviado',
      delivered: 'Entregado',
      opened: 'Abierto',
      clicked: 'Clic',
      bounced: 'Rebotado',
    };
    return <Badge variant={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Campaña no encontrada</p>
        <Button variant="link" asChild>
          <Link to="/marketing">Volver a Marketing</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/marketing">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{campaign.name}</h1>
              {getStatusBadge(campaign.status)}
            </div>
            <p className="text-muted-foreground">
              {campaign.service_type?.name || 'Sin tipo de servicio'}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          {campaign.status === 'draft' && (
            <>
              <Button variant="destructive" onClick={handleCancel} disabled={isCancelling}>
                <Trash2 className="h-4 w-4 mr-2" />
                Cancelar
              </Button>
              <Button onClick={handleSend} disabled={isSending}>
                <Send className="h-4 w-4 mr-2" />
                Enviar campaña
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats (only for sent campaigns) */}
      {campaign.status === 'sent' && <CampaignStats campaign={campaign} />}

      {/* Campaign Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Información</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Estado</p>
                <p className="font-medium">{getStatusBadge(campaign.status)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Destinatarios</p>
                <p className="font-medium">{campaign.sent_count}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Creada</p>
                <p className="font-medium">{formatDate(campaign.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Enviada</p>
                <p className="font-medium">{formatDate(campaign.sent_at)}</p>
              </div>
            </div>

            {campaign.subject && (
              <div>
                <p className="text-sm text-muted-foreground">Asunto</p>
                <p className="font-medium">{campaign.subject}</p>
              </div>
            )}

            {campaign.created_by && (
              <div>
                <p className="text-sm text-muted-foreground">Creada por</p>
                <p className="font-medium">{campaign.created_by.name}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {campaign.template_content && (
          <EmailPreview
            subject={campaign.subject || ''}
            htmlContent={campaign.template_content}
          />
        )}
      </div>

      {/* Recipients */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Destinatarios</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingRecipients ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : recipients.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              No hay destinatarios
            </p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Vehículo</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Entregado</TableHead>
                    <TableHead>Abierto</TableHead>
                    <TableHead>Clic</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recipients.map((recipient) => (
                    <TableRow key={recipient.id}>
                      <TableCell>
                        <div className="font-medium">{recipient.customer.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {recipient.customer.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        {recipient.vehicle?.registration || '-'}
                      </TableCell>
                      <TableCell>
                        {getRecipientStatusBadge(recipient.email_status)}
                      </TableCell>
                      <TableCell>{formatDate(recipient.delivered_at)}</TableCell>
                      <TableCell>{formatDate(recipient.opened_at)}</TableCell>
                      <TableCell>{formatDate(recipient.clicked_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
